'use client'

/**
 * UpdateBanner
 * Tooltip/toast no canto inferior direito avisando que existe uma nova
 * versão do `ccmakercode`. Persiste o dismiss por versão em localStorage,
 * então o mesmo aviso não reaparece até sair uma versão nova.
 */

import React, { useEffect, useState } from 'react'
import api from '../lib/api'

const STORAGE_KEY = 'ccmakers-dismissed-version'

export default function UpdateBanner() {
  const [version, setVersion] = useState(null)
  const [copied, setCopied] = useState(false)
  const [dismissed, setDismissed] = useState(true) // start hidden until we know

  useEffect(() => {
    let alive = true
    api.get('/api/app/version')
      .then(resp => {
        if (!alive) return
        const cli = resp?.cli
        if (!cli?.latest) return
        const seen = typeof window !== 'undefined'
          ? window.localStorage.getItem(STORAGE_KEY)
          : null
        setVersion(cli)
        setDismissed(seen === cli.latest)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const onDismiss = () => {
    if (version?.latest && typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, version.latest)
    }
    setDismissed(true)
  }

  const onCopy = async () => {
    if (!version?.installCommand) return
    try {
      await navigator.clipboard.writeText(version.installCommand)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  if (dismissed || !version) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        zIndex: 9998,
        width: 'min(380px, calc(100vw - 2rem))',
        background: 'rgba(10,14,18,0.96)',
        border: '1px solid rgba(0,255,65,0.35)',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 24px rgba(0,255,65,0.15)',
        fontFamily: 'var(--font-terminal, monospace)',
        color: 'var(--sc-text-primary, #fff)',
        overflow: 'hidden',
      }}
    >
      {/* header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0.75rem',
        borderBottom: '1px solid rgba(0,255,65,0.2)',
        background: 'rgba(0,255,65,0.08)',
      }}>
        <span style={{ color: 'var(--sc-green-bright, #00ff41)', fontSize: '0.875rem' }}>⬆</span>
        <span style={{
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          color: 'var(--sc-green-bright, #00ff41)',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}>
          nova versão disponível
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dispensar aviso"
          title="Não mostrar de novo"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--sc-text-muted, #888)',
            cursor: 'pointer',
            fontSize: '1rem',
            padding: 0,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>

      {/* body */}
      <div style={{ padding: '0.75rem' }}>
        <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          <strong style={{ color: 'var(--sc-green-bright, #00ff41)' }}>
            ccmakercode v{version.latest}
          </strong>
          {version.releasedAt && (
            <span style={{ color: 'var(--sc-text-ghost, #666)', marginLeft: '0.5rem', fontSize: '0.6875rem' }}>
              • {version.releasedAt}
            </span>
          )}
        </div>

        {Array.isArray(version.highlights) && version.highlights.length > 0 && (
          <ul style={{
            margin: '0 0 0.75rem 0',
            padding: 0,
            listStyle: 'none',
            fontSize: '0.6875rem',
            color: 'var(--sc-text-secondary, #bbb)',
          }}>
            {version.highlights.slice(0, 3).map((h, i) => (
              <li key={i} style={{ marginBottom: '2px' }}>
                <span style={{ color: 'var(--sc-green-muted, #4a6)' }}>›</span> {h}
              </li>
            ))}
          </ul>
        )}

        <div style={{
          background: 'rgba(0,0,0,0.4)',
          border: '1px solid rgba(0,255,65,0.18)',
          borderRadius: '6px',
          padding: '0.5rem 0.625rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
        }}>
          <span style={{ color: 'var(--sc-green-bright, #00ff41)', flexShrink: 0 }}>$</span>
          <code style={{
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: '0.6875rem',
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: 'rgba(255,255,255,0.92)',
          }}>
            {version.installCommand}
          </code>
          <button
            type="button"
            onClick={onCopy}
            aria-label="Copiar comando"
            title={copied ? 'Copiado!' : 'Copiar'}
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: 'none',
              color: copied ? 'var(--sc-green-bright, #00ff41)' : 'var(--sc-text-muted, #888)',
              cursor: 'pointer',
              fontSize: '0.75rem',
              padding: '2px',
            }}
          >
            {copied ? '✓' : '⧉'}
          </button>
        </div>

        <div style={{ fontSize: '0.6875rem', color: 'var(--sc-text-ghost, #666)' }}>
          cole no terminal para atualizar
        </div>
      </div>
    </div>
  )
}
